import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TrainRenderData } from '@/types';
import type { MapRef } from 'react-map-gl/mapbox';

import usePinnedItem from '@/hooks/use-pinned-item';
import {
  MAX_ZOOM_IN,
  PIN_BEARING_THRESHOLD,
  PIN_FOLLOW_INTERVAL_MS,
  PIN_POSITION_THRESHOLD,
} from '@/utils/constants/map';
import { SEARCH_PARAM_SLOW_SECTION_ID } from '@/utils/constants/queries';

export const useTrainPinAnimation = (
  map: MapRef | undefined | null,
  trainData: TrainRenderData[],
  lockedJourneyId: string | number | null
) => {
  const [searchParams] = useSearchParams();
  const mapInstance = map?.getMap();

  const prevLockedIdRef = useRef<string | null>(null);
  const isFlyingRef = useRef(false);
  const lastPositionRef = useRef<[number, number] | null>(null);
  const lastBearingRef = useRef<number | null>(null);

  const { pinnedItemType } = usePinnedItem();

  const isPinnedStation = pinnedItemType === 'station';

  const isSlowSection = !!searchParams.get(SEARCH_PARAM_SLOW_SECTION_ID);
  const wasSlowSectionRef = useRef(isSlowSection);

  const trainDataRef = useRef<TrainRenderData[]>(trainData);

  useEffect(() => {
    trainDataRef.current = trainData;
  }, [trainData]);

  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    const handleAnimationEnd = () => {
      isFlyingRef.current = false;
    };

    // Listeners for camera completion or user interruptions
    mapInstance.on('moveend', handleAnimationEnd);
    mapInstance.on('movecancel', handleAnimationEnd);
    mapInstance.on('dragstart', handleAnimationEnd);
    mapInstance.on('zoomstart', handleAnimationEnd);

    return () => {
      mapInstance.off('moveend', handleAnimationEnd);
      mapInstance.off('movecancel', handleAnimationEnd);
      mapInstance.off('dragstart', handleAnimationEnd);
      mapInstance.off('zoomstart', handleAnimationEnd);
    };
  }, [mapInstance]);

  const safeLockedId = lockedJourneyId !== null ? String(lockedJourneyId) : null;
  const hasTargetTrain = trainData.some((t) => String(t.id) === safeLockedId);

  // EFFECT 1: Handle Initial Lock-on, Refresh, and Returning from Slow Sections
  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    // CASE 1: Unhooked -> Camera positions to normal default
    if (!safeLockedId) {
      if (prevLockedIdRef.current) {
        isFlyingRef.current = true; // Mark as flying to avoid interval overrides
        prevLockedIdRef.current = null;
        lastPositionRef.current = null;
        lastBearingRef.current = null;

        if (!isPinnedStation) {
          mapInstance.stop();
          mapInstance.easeTo({
            pitch: 0,
            bearing: 0,
            duration: 2000,
          });
        }
      }

      return;
    }

    // CASE 2: Currently viewing a slow section
    if (isSlowSection) {
      wasSlowSectionRef.current = isSlowSection;

      return;
    }

    const isNewLock = prevLockedIdRef.current !== safeLockedId;
    const returnedFromSlowSection = wasSlowSectionRef.current && !isSlowSection;
    wasSlowSectionRef.current = isSlowSection;

    if (!isNewLock && !returnedFromSlowSection) {
      return;
    }

    // CASE 3: Hooked for the first time, Switched Trains, OR Returned from Slow Section
    const lockedTrain = trainDataRef.current.find((t) => String(t.id) === safeLockedId);

    if (lockedTrain) {
      isFlyingRef.current = true;
      prevLockedIdRef.current = safeLockedId;
      lastPositionRef.current = [lockedTrain.position[0], lockedTrain.position[1]];
      lastBearingRef.current = lockedTrain.bearing ?? 0;

      mapInstance.stop();
      mapInstance.flyTo({
        center: [lockedTrain.position[0], lockedTrain.position[1]],
        zoom: isNewLock ? MAX_ZOOM_IN : mapInstance.getZoom(),
        bearing: lockedTrain.bearing ?? 0,
        pitch: 0,
        duration: 2000,
      });
    }
  }, [mapInstance, lockedJourneyId, isSlowSection, hasTargetTrain]);

  // EFFECT 2: Handle Polling (Interval tracking)
  useEffect(() => {
    if (!mapInstance || !safeLockedId || isSlowSection) {
      return;
    }

    const followTick = () => {
      // Guard against running updates while flyTo is active or user is interacting
      const isUserDragging = mapInstance.dragPan?.isActive() || mapInstance.dragRotate?.isActive();

      if (isFlyingRef.current || mapInstance.isZooming() || isUserDragging) {
        return;
      }

      const train = trainDataRef.current.find((t) => String(t.id) === safeLockedId);

      if (!train) {
        return;
      }

      const newLng = train.position[0];
      const newLat = train.position[1];
      const newBearing = train.bearing ?? 0;

      const lastPos = lastPositionRef.current;
      const lastBearing = lastBearingRef.current;

      const posChanged =
        !lastPos ||
        Math.abs(newLng - lastPos[0]) > PIN_POSITION_THRESHOLD ||
        Math.abs(newLat - lastPos[1]) > PIN_POSITION_THRESHOLD;

      const bearingChanged =
        lastBearing === null || Math.abs(newBearing - lastBearing) > PIN_BEARING_THRESHOLD;

      if (!posChanged && !bearingChanged) {
        return;
      }

      lastPositionRef.current = [newLng, newLat];
      lastBearingRef.current = newBearing;

      mapInstance.easeTo({
        center: [newLng, newLat],
        bearing: newBearing,
        pitch: 0,
        duration: 800,
        easing: (t) => t,
      });
    };

    if (!isFlyingRef.current) {
      followTick();
    }

    const followInterval = setInterval(followTick, PIN_FOLLOW_INTERVAL_MS);

    return () => clearInterval(followInterval);
  }, [mapInstance, lockedJourneyId, isSlowSection]);
};
